import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, throwError, forkJoin, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConsultarLineasNegocioResponse } from '../../models/cotizador-programas/lineas-negocio/consultar-lineas-negocio-response.model';
import { ConsultarGruposProgramasResponse } from '../../models/cotizador-programas/grupos-activos/consultar-grupos-programas-response.model';
import { ConsultarProgramasActivosResponse } from '../../models/cotizador-programas/programas-activos/consultar-programas-activos-response.model';
import { ConsultarCarnetsRequest } from '../../models/cotizador-programas/carnets/request/consultar-carnets-request.model';
import { ConsultarCarnetsResponse } from '../../models/cotizador-programas/carnets/response/consultar-carnets-response.model';
import { ConsultarProgramaHomologoAsociadoRequest } from '../../models/cotizador-programas/carnets/request/consultar-programa-homologo-asociado-request.model';
import { ConsultarProgramaHomologoAsociadoResponse } from '../../models/cotizador-programas/carnets/response/consultar-programa-homologo-asociado-response.model';
import { ConsultarProgramasByParametroRequest } from '../../models/cotizador-programas/programas-by-parametro/request/consultar-programas-by-parametro-request.model';
import { ConsultarProgramasByParametroResponse } from '../../models/cotizador-programas/programas-by-parametro/response/consultar-programas-by-parametro-response.model';
import { URL_MAP } from '../../shared/urlMap';
import { environment } from '../../../environments/environment';

export interface ProgramaCompleto {
  codigo: string;
  programa: string;
  carnet?: {
    file: string;
    code: string;
    program: string;
  };
}

export interface GrupoCompleto {
  codigo: string;
  grupo_programa: string;
  programas: ProgramaCompleto[];
}

export interface ProgramasPorLineaResponse {
  grupos: GrupoCompleto[];
}

@Injectable({
  providedIn: 'root'
})
export class ConsultarLineasGruposProgramasService {
  private readonly apiUrlLineas = `${environment.HOST}${URL_MAP.API.CONSULTAR_LINEAS_NEGOCIO}`;
  private readonly apiUrlGrupos = `${environment.HOST}${URL_MAP.API.CONSULTAR_GRUPOS_PROGRAMAS_ACTIVOS}`;
  private readonly apiUrlProgramas = `${environment.HOST}${URL_MAP.API.CONSULTAR_PROGRAMAS_ACTIVOS}`;
  private readonly apiUrlCarnets = `${environment.HOST}${URL_MAP.API.CONSULTAR_PROGRAMAS_CARNETS}`;
  private readonly apiUrlProgramaHomologoAsociado =
    `${environment.HOST}${URL_MAP.API.CONSULTAR_PROGRAMA_HOMOLOGO_ASOCIADO}`;
  private readonly apiUrlProgramasByParametro = `${environment.HOST}${URL_MAP.API.CONSULTAR_PROGRAMAS_BY_PARAMETRO}`;

  private programasCompletosCache: Map<string, ProgramasPorLineaResponse> = new Map();
  private programasPorParametroCache: Map<string, ProgramasPorLineaResponse> = new Map();
 
 /* private readonly nombresResumidosPorLinea: Record<string, Record<string, string>> = {
    MI: {
      'ORO PLUS': 'ORO PLUS',
      'ORO PRIME': 'ORO PRIME',
      'PLATA PRIME': 'PLATA PRIME',
      'PLATA JOVEN': 'PLATA JOVEN',
      'PREFERENTE': 'PREFERENTE',
      'TRADICIONAL ESPECIAL': 'TRADICIONAL ESPECIAL',
      'AMBULATORIO ESPECIAL': 'AMBULATORIO ESPECIAL',
      'AMBULATORIO ESENCIAL': 'AMBULATORIO ESENCIAL'
    },
    SAO: {
      'DENTAL ELITE': 'DENTAL ÉLITE',
      'DENTAL ESPECIAL': 'DENTAL ESPECIAL',
      'DENTAL EXPRESS': 'DENTAL EXPRESS'
    },
    CEM: {
      'CEM FULL': 'CEM FULL'
    }
  };
*/

  constructor(private httpClient: HttpClient) { }


  /**
   * Estandariza el nombre del programa recibido desde el backend.
   * Aplica mayúsculas, elimina espacios residuales y corrige posibles 
   * problemas de codificación UTF-8 durante la transferencia HTTP.
   */
  private formatearNombrePrograma(nombre: string): string {
    if (!nombre) {
      return '';
    }

    let nombreFormateado = nombre.trim().toUpperCase();

    const mapaCorreccionCaracteres: Record<string, string> = {
      'Ã‘': 'Ñ',
      'Ã¡': 'Á', 'Ã ': 'Á', 'A´': 'Á',
      'Ã©': 'É', 'Ãˆ': 'É', 'E´': 'É',
      'Ã\u00AD': 'Í', 'ÃŒ': 'Í', 'I´': 'Í',
      'Ã³': 'Ó', 'Ã’': 'Ó', 'O´': 'Ó',
      'Ãº': 'Ú', 'Ã™': 'Ú', 'U´': 'Ú'
    };

    for (const [caracterCorrupto, caracterValido] of Object.entries(mapaCorreccionCaracteres)) {
      if (nombreFormateado.includes(caracterCorrupto)) {
        nombreFormateado = nombreFormateado.split(caracterCorrupto).join(caracterValido);
      }
    }

    return nombreFormateado;
  }


  private normalizarTextoPrograma(value: string): string {
    return (value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toUpperCase();
  }
  /*

  private resumirNombrePrograma(program: string, linea: 'MI' | 'SAO' | 'CEM'): string {
    const original = (program || '').trim();
    const normalizado = this.normalizarTextoPrograma(original);
    const mapa = this.nombresResumidosPorLinea[linea] || {};

    for (const [clave, valor] of Object.entries(mapa)) {
      if (normalizado.includes(clave)) {
        return valor;
      }
    }

    return original;
  }
*/
   private resumirNombrePrograma(program: string, linea: 'MI' | 'SAO' | 'CEM'): string {
 
    return this.formatearNombrePrograma(program);
  }

  consultarLineasNegocio(): Observable<ConsultarLineasNegocioResponse> {
    return this.httpClient.get<ConsultarLineasNegocioResponse>(this.apiUrlLineas).pipe(
      catchError(error => {
        console.error('Error en consultarLineasNegocio: ', error);
        return throwError(() => new Error(error?.message || 'Error al consultar líneas de negocio'));
      })
    );
  }

  consultarGruposProgramasActivos(lineaNegocio: number): Observable<ConsultarGruposProgramasResponse> {
    const params = new HttpParams().set('lineaNegocio', lineaNegocio.toString());
    return this.httpClient.get<ConsultarGruposProgramasResponse>(this.apiUrlGrupos, { params }).pipe(
      catchError(error => {
        console.error('Error en consultarGruposProgramasActivos: ', error);
        return throwError(() => new Error(error?.message || 'Error al consultar grupos de programas activos'));
      })
    );
  }

  consultarProgramasActivos(grupoPrograma: string): Observable<ConsultarProgramasActivosResponse> {
    const params = new HttpParams().set('grupoPrograma', grupoPrograma);
    return this.httpClient.get<ConsultarProgramasActivosResponse>(this.apiUrlProgramas, { params }).pipe(
      catchError(error => {
        console.error('Error en consultarProgramasActivos: ', error);
        return throwError(() => new Error(error?.message || 'Error al consultar programas activos'));
      })
    );
  }

  consultarCarnets(request: ConsultarCarnetsRequest): Observable<ConsultarCarnetsResponse> {
    return this.httpClient.post<ConsultarCarnetsResponse>(this.apiUrlCarnets, request).pipe(
      catchError(error => {
        console.error('Error en consultarCarnets: ', error);
        return throwError(() => new Error(error?.message || 'Error al consultar carnets'));
      })
    );
  }

  consultarProgramaHomologoAsociado(
    request: ConsultarProgramaHomologoAsociadoRequest
  ): Observable<ConsultarProgramaHomologoAsociadoResponse> {
    return this.httpClient
      .post<ConsultarProgramaHomologoAsociadoResponse>(this.apiUrlProgramaHomologoAsociado, request)
      .pipe(
        catchError(error => {
          console.error('Error en consultarProgramaHomologoAsociado:', error);
          return throwError(
            () => new Error(error?.message || 'Error al consultar programa homologo asociado')
          );
        })
      );
  }

  consultarProgramasByParametro(request: ConsultarProgramasByParametroRequest): Observable<ConsultarProgramasByParametroResponse> {
    return this.httpClient.post<ConsultarProgramasByParametroResponse>(this.apiUrlProgramasByParametro, request).pipe(
      catchError(error => {
        console.error('Error en consultarProgramasByParametro: ', error);
        return throwError(() => new Error(error?.message || 'Error al consultar programas por parámetro'));
      })
    );
  }

  consultarProgramasCompletosPorLinea(lineaNegocio: number, forzarActualizacion: boolean = false, tipoPlan: string = '', edad: number = 0): Observable<ProgramasPorLineaResponse> {
    const cacheKey = `${lineaNegocio}_${tipoPlan}_${edad}`;

    if (!forzarActualizacion && this.programasCompletosCache.has(cacheKey)) {
      console.log('consultarProgramasCompletosPorLinea - Retornando datos desde cache para key: ', cacheKey);
      return of(this.programasCompletosCache.get(cacheKey)!);
    }

    const lineaStr = lineaNegocio === 1 ? 'MI' : lineaNegocio === 2 ? 'SAO' : 'CEM';
    const nombreLinea = lineaNegocio === 1 ? 'Medicina Integral' : lineaNegocio === 2 ? 'Salud Oral' : 'Emergencia Médica';

   const requestCarnets: ConsultarCarnetsRequest = { lineaNegocio: [lineaStr], tipoPlan: tipoPlan, edad: edad };

    console.log('consultarProgramasCompletosPorLinea - Request enviado a consultarCarnets: ', JSON.stringify(requestCarnets));

    return this.consultarCarnets(requestCarnets).pipe(
      map((carnetsResponse) => {
        const carnets = carnetsResponse.status && carnetsResponse.data?.data ? carnetsResponse.data.data : [];
        console.log('consultarProgramasCompletosPorLinea - Respuesta recibida. Cantidad de carnets: ', carnets.length);

        const programasCompletos: ProgramaCompleto[] = carnets.map(c => {
          let base64File = c.file;
          const programResumen = this.resumirNombrePrograma((c.program || '').trim(), lineaStr as 'MI' | 'SAO' | 'CEM');
          if (base64File && !base64File.startsWith('data:')) {
            base64File = 'data:image/png;base64,' + base64File;
          }

          return {
            codigo: (c.code || '').trim(),
            programa: programResumen,
            carnet: {
              file: base64File,
              code: (c.code || '').trim(),
              program: programResumen
            }
          };
        });

        const respuesta: ProgramasPorLineaResponse = {
          grupos: [{ codigo: lineaNegocio.toString(), grupo_programa: nombreLinea, programas: programasCompletos }]
        };

        console.log('consultarProgramasCompletosPorLinea - Estructura final mapeada: ', respuesta);
        this.programasCompletosCache.set(cacheKey, respuesta);
        return respuesta;
      }),
      catchError(error => {
        console.error('Error en consultarProgramasCompletosPorLinea directo: ', error);
        return throwError(() => new Error(error?.message || 'Error al consultar programas completos por línea'));
      })
    );
  }

  consultarProgramasCompletosPorParametro(forzarActualizacion: boolean = false, tipoPlan: string = '', edad: number = 0): Observable<ProgramasPorLineaResponse> {
    const cacheKey = `${tipoPlan || 'DEFAULT'}_${edad}`;

    // Validar entrada y cache
    console.log(`[Service] Iniciando consulta por parámetro. Plan: ${tipoPlan}, Edad: ${edad}, Forzar: ${forzarActualizacion}`);

    if (!forzarActualizacion && this.programasPorParametroCache.has(cacheKey)) {
      console.log(`[Service] Retornando datos desde CACHE para key: ${cacheKey}`);
      return of(this.programasPorParametroCache.get(cacheKey)!);
    }

    const lineasConfig = [
      { param: 'MI', cod: '1', nombre: 'Medicina Integral' },
      { param: 'SAO', cod: '2', nombre: 'Salud Oral' },
      { param: 'CEM', cod: '3', nombre: 'Emergencia Médica' }
    ];

    // Mapeamos las peticiones
    const peticiones = lineasConfig.map(linea => {
    const requestCarnets: ConsultarCarnetsRequest = { lineaNegocio: [linea.param], tipoPlan: tipoPlan, edad: edad };


      return this.consultarCarnets(requestCarnets).pipe(
        map(res => {
          const carnets = res.status && res.data?.data ? res.data.data : [];
          //  Verificación de respuesta por línea
          console.log(`[Service] Linea ${linea.param}: Recibidos ${carnets.length} carnets.`);

          const programasMapped: ProgramaCompleto[] = carnets.map(c => {
            let base64File = c.file;
            const programResumen = this.resumirNombrePrograma((c.program || '').trim(), linea.param as 'MI' | 'SAO' | 'CEM');
            
            // Debug de imagen (solo si viene vacía)
            if (!base64File) {
              console.warn(`[Service] Alerta: El programa ${c.code} no trae imagen (file vacio).`);
            }

            if (base64File && !base64File.startsWith('data:')) {
              base64File = 'data:image/png;base64,' + base64File;
            }

            return {
              codigo: (c.code || '').trim(),
              programa: programResumen,
              carnet: { 
                file: base64File, 
                code: (c.code || '').trim(), 
                program: programResumen 
              }
            };
          });

          return {
            codigo: linea.cod,
            grupo_programa: linea.nombre,
            programas: programasMapped
          } as GrupoCompleto;
        }),
        catchError((error) => {
          console.error(`[Service] Error fatal en linea ${linea.param}:`, error);
          return of({ codigo: linea.cod, grupo_programa: linea.nombre, programas: [] } as GrupoCompleto);
        })
      );
    });

    return forkJoin(peticiones).pipe(
      map(grupos => {
        const respuesta: ProgramasPorLineaResponse = { grupos };
        const totalProgramas = grupos.reduce((acc, g) => acc + g.programas.length, 0);
        console.log(`[Service] Proceso finalizado. Total programas mapeados: ${totalProgramas}`);
        
        this.programasPorParametroCache.set(cacheKey, respuesta);
        return respuesta;
      })
    );
  }

  consultarProgramasCompletosPorParametroPorLinea(lineaNegocio: number, forzarActualizacion: boolean = false, tipoPlan: string = '', edad: number = 0): Observable<ProgramasPorLineaResponse> {
    return this.consultarProgramasCompletosPorParametro(forzarActualizacion, tipoPlan, edad).pipe(
      map(full => ({ grupos: full.grupos.filter(g => g.codigo === String(lineaNegocio)) }))
    );
  }

  limpiarCacheProgramas(): void {
    this.programasCompletosCache.clear();
    this.programasPorParametroCache.clear();
  }

  limpiarCacheProgramasPorLinea(lineaNegocio: number): void {
    const keys = Array.from(this.programasCompletosCache.keys());
    keys.forEach(key => {
      if (key.startsWith(`${lineaNegocio}_`)) {
        this.programasCompletosCache.delete(key);
      }
    });
  }
}
